import { type QueryClient, queryOptions } from "@tanstack/react-query";
import { getBuddyRelationships } from "@/actions/buddy";
import type { BuddyRelationship } from "./relationship";
import { buddyRelationshipKeys } from "./relationship-cache";

type Pending = {
  userId: string;
  resolve: (relationship: BuddyRelationship) => void;
  reject: (error: Error) => void;
};
const queues = new WeakMap<QueryClient, Pending[]>();

// Individual buttons share one entry per person, while reads for a whole
// roster are batched into one server action (at most 200 IDs per request).
function fetchRelationship(
  client: QueryClient,
  userId: string,
  signal: AbortSignal,
) {
  return new Promise<BuddyRelationship>((resolve, reject) => {
    const existing = queues.get(client);
    const queue = existing ?? [];
    const entry: Pending = {
      userId,
      resolve: (relationship) => {
        signal.removeEventListener("abort", abort);
        resolve(relationship);
      },
      reject: (error) => {
        signal.removeEventListener("abort", abort);
        reject(error);
      },
    };
    const abort = () => {
      const index = queue.indexOf(entry);
      if (index >= 0) queue.splice(index, 1);
      entry.reject(new Error("Connection status lookup canceled"));
    };
    queue.push(entry);
    signal.addEventListener("abort", abort, { once: true });
    if (signal.aborted) abort();
    if (existing) return;
    queues.set(client, queue);
    setTimeout(async () => {
      queues.delete(client);
      const ids = [...new Set(queue.map((entry) => entry.userId))];
      for (let offset = 0; offset < ids.length; offset += 200) {
        const chunk = ids.slice(offset, offset + 200);
        const pending = queue.filter((entry) => chunk.includes(entry.userId));
        try {
          const result = await getBuddyRelationships(chunk);
          if (!result.ok)
            throw new Error(
              result.message ?? "Unable to load connection status",
            );
          for (const entry of pending)
            entry.resolve(result.data[entry.userId] ?? "none");
        } catch (error) {
          for (const entry of pending)
            entry.reject(
              error instanceof Error
                ? error
                : new Error("Unable to load connection status"),
            );
        }
      }
    }, 0);
  });
}

export function buddyRelationshipOptions(
  client: QueryClient,
  userId: string,
  initialData?: BuddyRelationship,
) {
  return queryOptions({
    queryKey: buddyRelationshipKeys.user(userId),
    queryFn: ({ signal }) => fetchRelationship(client, userId, signal),
    initialData,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });
}
